/*
 * Visitor-level Optimizely attributes the browser knows: device, browser,
 * browser language/locale, session entry (referrer, source, campaign) and
 * returning_user. Captured once per session and reused by both the decisions
 * call (for bucketing) and every event, so the two always agree.
 */
import {isFirstVisit} from './optimizely-visitor-id'

const SESSION_KEY = 'gdi_visitor_ctx'
const MAX_LEN = 200
const SEARCH_ENGINES = /(^|\.)(google|bing|yahoo|duckduckgo|baidu|yandex|ecosia|ask)\./i
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'fbclid', 'ttclid']

let sessionContext = null
let serverContext = {}

const trim = (value) => (typeof value === 'string' ? value.slice(0, MAX_LEN) : value)

/** Pure: device and browser family from a user-agent string. Isomorphic. */
export const deriveUserAgentAttributes = (userAgent) => {
    const ua = String(userAgent || '')
    const isTablet = /iPad|Tablet|PlayBook|Silk|Android(?!.*Mobile)/i.test(ua)
    const isMobile = !isTablet && /Mobi|iPhone|iPod|Windows Phone|Opera Mini/i.test(ua)
    let browser = 'other'
    if (/Edg(e|A|iOS)?\//i.test(ua)) browser = 'edge'
    else if (/OPR\/|Opera/i.test(ua)) browser = 'opera'
    else if (/SamsungBrowser/i.test(ua)) browser = 'samsung'
    else if (/Chrome\/|CriOS\//i.test(ua)) browser = 'chrome'
    else if (/Firefox\/|FxiOS\//i.test(ua)) browser = 'firefox'
    else if (/Safari\//i.test(ua)) browser = 'safari'
    let device = 'desktop'
    if (isTablet) device = 'tablet'
    else if (isMobile) device = 'mobile'
    return {device, browser}
}

const readSession = () => {
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const writeSession = (value) => {
    try {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(value))
    } catch {
        // storage blocked — the in-memory copy still covers this page load
    }
}

const captureEntry = () => {
    const url = new URL(window.location.href)
    const hasCampaign = CAMPAIGN_PARAMS.some((key) => url.searchParams.has(key))
    let referrer = ''
    let referrerHost = ''
    try {
        const ref = document.referrer ? new URL(document.referrer) : null
        if (ref && ref.host !== window.location.host) {
            referrer = ref.href
            referrerHost = ref.host
        }
    } catch {
        // malformed referrer — treat as none
    }
    let source = 'referral'
    if (hasCampaign) source = 'campaign'
    else if (!referrer) source = 'direct'
    else if (SEARCH_ENGINES.test(referrerHost)) source = 'search'
    return {
        referrer: trim(referrer),
        source,
        campaign: trim(url.searchParams.get('utm_campaign') || ''),
        returning_user: !isFirstVisit()
    }
}

/**
 * The browser-known visitor attributes, stable for the session. Returns {}
 * during SSR.
 * @returns {{device: string, browser: string, language: string, locale: string, referrer: string, source: string, campaign: string, returning_user: boolean}}
 */
export const getVisitorContext = () => {
    if (typeof window === 'undefined') return {}
    if (sessionContext) return sessionContext
    let entry = readSession()
    if (!entry) {
        entry = captureEntry()
        writeSession(entry)
    }
    const browserLocale = String(window.navigator?.language || '')
    sessionContext = {
        ...deriveUserAgentAttributes(window.navigator?.userAgent),
        language: browserLocale.split('-')[0].toLowerCase(),
        locale: browserLocale,
        ...entry
    }
    return sessionContext
}

/**
 * Attributes only the server can derive (geo country), as returned by
 * /optimizely-decisions. Persisted with the session so later page loads carry
 * them before the decisions call returns.
 */
export const setServerVisitorContext = (attributes) => {
    if (typeof window === 'undefined') return
    serverContext = attributes && typeof attributes === 'object' ? attributes : {}
    const entry = readSession()
    if (entry) writeSession({...entry, ...serverContext})
    if (sessionContext) sessionContext = {...sessionContext, ...serverContext}
}

/** Everything visitor-level, client + server derived, for the FX user context. */
export const getVisitorAttributes = () => ({...getVisitorContext(), ...serverContext})
